/*
 * Borow — colour selector restyle (Rubik Combined Listings)
 *
 * The app renders <rubik-combined-listings> into an OPEN shadow root, so theme
 * CSS can't reach .rcl__* from the page. Two things happen here:
 *
 *   1. A stylesheet is adopted into that shadow root, laying the group label out
 *      as the mockup has it — "COLOR" small-caps on the left, the selected value
 *      right-aligned opposite it, no colon — and sizing the swatches. Every
 *      value is a custom property set from the light DOM in
 *      borow-colour-selector.css, so the theme's colour scheme still owns the ink.
 *   2. The block and the product info column are tagged once swatches actually
 *      exist, so the hairline above the colour row (and the price row dropping
 *      its own) only apply on products that really are in a colour group. A tag,
 *      not :has(), because the app block element is present-but-empty on a
 *      product that is not grouped.
 *
 * adoptedStyleSheets, not a <style> element: a shadow root's own style elements
 * are ordered BEFORE its adopted sheets, so a <style> appended here loses every
 * specificity tie to the app's sheet — width, gap and font-size all silently
 * stayed at the app's values. Adopting last wins those ties without !important.
 * Most of the work is done by overriding the app's own --rcl-* tokens; only the
 * label layout, which has no token, is restyled by rule.
 *
 * If the app changes its internals the styles simply stop matching; nothing here
 * is load-bearing for the selector working.
 */
(function () {
  var CSS = [
    ':host {',
    '  --rcl-swatch-width: var(--borow-rcl-swatch, 28px);',
    '  --rcl-grid-gap: var(--borow-rcl-gap, 16px);',
    '  --rcl-group-margin-bottom: 0;',
    '  --rcl-margin-top: 0;',
    '  --rcl-margin-bottom: 0;',
    '  --rcl-swatch-border-color: var(--borow-rcl-bg, #fff);',
    '  --rcl-swatch-active-border-color: var(--borow-rcl-fg, #121212);',
    '  --rcl-swatch-unavailable-opacity: var(--borow-rcl-unavailable, 0.4);',
    '}',
    /* The mockup writes the label and its value as two columns, not "Color: X". */
    '.rcl__group-label {',
    '  display: flex;',
    '  align-items: baseline;',
    '  justify-content: space-between;',
    '  gap: 1.2rem;',
    '  margin: 0 0 var(--borow-rcl-label-gap, 1.1rem);',
    '}',
    '.rcl__group-label-option-name {',
    '  font-size: var(--borow-rcl-label-size, 1rem);',
    '  font-weight: 400;',
    '  letter-spacing: 0.13em;',
    '  text-transform: uppercase;',
    '  color: var(--borow-rcl-fg, #121212);',
    '}',
    '.rcl__group-label-separator { display: none; }',
    '.rcl__group-label-selected-value {',
    '  font-size: var(--borow-rcl-value-size, 1.1rem);',
    '  font-weight: 400;',
    '  letter-spacing: 0.05em;',
    '  text-transform: none;',
    '  text-align: right;',
    '  color: var(--borow-rcl-muted, rgba(18, 18, 18, 0.74));',
    '}',
    '.rcl__grid {',
    '  gap: var(--borow-rcl-gap, 16px);',
    '  padding: 2px 1px;',
    '}',
    /* Hairline ring, background-coloured gap, colour fill — the app's own
       construction, just larger and keyed to the theme's ink. */
    '.rcl__swatch {',
    '  width: var(--borow-rcl-swatch, 28px);',
    '  height: var(--borow-rcl-swatch, 28px);',
    '  box-shadow: 0 0 0 1px var(--borow-rcl-hairline, rgba(18, 18, 18, 0.25));',
    '}',
    '.rcl__swatch-media {',
    '  width: calc(var(--borow-rcl-swatch, 28px) - 4px);',
    '  height: calc(var(--borow-rcl-swatch, 28px) - 4px);',
    '}',
    '.rcl__swatch--active {',
    '  box-shadow: 0 0 0 1px var(--borow-rcl-fg, #121212);',
    '}'
  ].join('\n');

  var MARK = 'data-borow-rcl';
  var sheet = null;

  function build() {
    if (sheet !== null) return sheet;
    try {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
    } catch (e) {
      sheet = false; // no constructable stylesheets — fall back below
    }
    return sheet;
  }

  function adopt(root) {
    var s = build();
    if (s) {
      var list = root.adoptedStyleSheets || [];
      // Only re-adopt when it isn't already the last sheet, so this is a no-op
      // on every tick after the first.
      if (list[list.length - 1] === s) return;
      root.adoptedStyleSheets = list.filter(function (x) { return x !== s; }).concat(s);
      return;
    }
    // Fallback path only: a <style> here is outranked by the app's sheet, so
    // these declarations have to be marked important to land at all.
    if (root.querySelector('style[' + MARK + ']')) return;
    var el = document.createElement('style');
    el.setAttribute(MARK, '');
    el.textContent = CSS.replace(/;/g, ' !important;');
    root.appendChild(el);
  }

  var watched = new WeakSet();

  function apply() {
    var hosts = document.querySelectorAll('rubik-combined-listings');
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var root = host.shadowRoot;
      if (!root) continue;

      adopt(root);

      // The app re-renders the root on a variant change; re-adopt if it does.
      if (!watched.has(root)) {
        watched.add(root);
        new MutationObserver(function (records) {
          adopt(records[0].target);
        }).observe(root, { childList: true });
      }

      var grouped = !!root.querySelector('.rcl__swatch');
      var block = host.closest('.shopify-app-block');
      if (block) block.classList.toggle('borow-colour-block', grouped);
      var info = host.closest('.product__info-container');
      if (info) info.classList.toggle('borow-has-colour', grouped);
    }
    return hosts.length > 0;
  }

  // The app block hydrates after the theme's own scripts and fires no event to
  // hook, so poll briefly and stop as soon as the selector is there.
  var tries = 0;
  var timer = setInterval(function () {
    if (apply() || ++tries > 40) clearInterval(timer);
  }, 150);
  apply();
  document.addEventListener('DOMContentLoaded', apply);
  window.addEventListener('load', apply);
})();